import { debug } from './logger'

export const scrollToTop = () => {
  debug('[utilities][window.scrollToTop]')
  window.scrollTo(0, 0)
}

export const injectScript = ({
  url,
  text,
  defer,
  isAsync,
  type = 'text/javascript',
  tag = 'head'
}) => {
  if (!url && !text) {
    return debug(`[utilities][window.injectScript] Unable to inject script without script or text`)
  }
  const head = document.getElementsByTagName(tag)[0]
  const script = document.createElement('script')
  script.type = type
  if (isAsync) {
    script.async = true
  }
  if (defer) {
    script.defer = true
  }
  if (url) {
    script.src = url
  }
  if (text) {
    script.text = text
  }
  head.appendChild(script)
  return script
}

export const removeScript = (url) => {
  const scripts = document.getElementsByTagName('script')
  for (let i = scripts.length; i--;) {
    let script = scripts[i]
    if (script.src === url) {
      script.parentNode.removeChild(script)
    }
  }
  return false
}

export const removeCanonicalLink = () => {
  const canonicalLink = document.querySelector('link[rel="canonical"]')
  if (canonicalLink) {
    canonicalLink.remove()
  }
}

export const updateCanonicalLink = (url) => {
  if (!url) {
    return
  }

  // Clean up URLs to avoid duplicate slashes
  const cleanUrl = url.startsWith('/') ? url : `/${url}`
  const encodedUrl = encodeURI(cleanUrl)
  const fullUrl = `https://www.cmonjardinier.com/app${encodedUrl}`

  const existingLink = document.querySelector('link[rel="canonical"]')
  if (existingLink) {
    existingLink.setAttribute('href', fullUrl)
  } else {
    const link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    link.setAttribute('href', fullUrl)
    document.head.appendChild(link)
  }
}

export const addNoIndexIfNotExists = () => {
  const existingMeta = document.querySelector('meta[name="robots"]')
  if (!existingMeta) {
    const meta = document.createElement('meta')
    meta.setAttribute('name', 'robots')
    meta.setAttribute('content', 'noindex')
    document.head.appendChild(meta)
  }
}

export const removeNoIndexIfExists = () => {
  const existingMeta = document.querySelector('meta[name="robots"]')
  if (existingMeta) {
    existingMeta.remove()
  }
}

export const pushGtmTrigger = (event) => {
  try {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: event
      })
    }
  } catch (error) {
    debug(`[utilities][window.pushGtmTrigger] Error: ${error}`)
  }
}

export const cleanAvisVerifieFlag = () => {
  window.tagjsNetreviews = undefined
}

/**
 * Complete, robust SEO metadata injection
 */
export const injectSEOMetadata = (metadata, context) => {
  context = context || {}

  const results = {
    title: false,
    description: false,
    keywords: false,
    canonical: false,
    errors: []
  }

  try {
    // 1. Title injection
    if (metadata.title && !metadata.title.includes('undefined')) {
      document.title = metadata.title
      results.title = true
      debug(`[injectSEOMetadata] Title updated: "${metadata.title.substring(0, 50)}..."`)
    } else {
      results.errors.push('Title is undefined or invalid')
    }

    // 2. Description injection
    if (metadata.description && !metadata.description.includes('undefined')) {
      var descMeta = document.querySelector('meta[name="description"]')
      if (!descMeta) {
        descMeta = document.createElement('meta')
        descMeta.setAttribute('name', 'description')
        document.head.appendChild(descMeta)
      }
      descMeta.setAttribute('content', metadata.description)
      results.description = true
      debug(`[injectSEOMetadata] Description updated`)
    } else {
      results.errors.push('Description is undefined or invalid')
    }

    // 3. Keyword injection
    if (metadata.keywords && !metadata.keywords.includes('undefined')) {
      var keywordsMeta = document.querySelector('meta[name="keywords"]')
      if (!keywordsMeta) {
        keywordsMeta = document.createElement('meta')
        keywordsMeta.setAttribute('name', 'keywords')
        document.head.appendChild(keywordsMeta)
      }
      keywordsMeta.setAttribute('content', metadata.keywords)
      results.keywords = true
      debug(`[injectSEOMetadata] Keywords updated`)
    }

    // 4. Canonical injection (if supplied)
    if (metadata.canonical && !metadata.canonical.includes('undefined')) {
      updateCanonicalLink(metadata.canonical)
      results.canonical = true
    } else if (context.canonical) {
      updateCanonicalLink(context.canonical)
      results.canonical = true
    }
  } catch (error) {
    const errorMsg = `Failed to inject SEO metadata: ${error.message}`
    results.errors.push(errorMsg)
    console.error('[injectSEOMetadata]', errorMsg)
  }

  // Log of comprehensive income
  const successCount = Object.keys(results).filter(function (key) {
    return key !== 'errors' && results[key] === true
  }).length
  debug(`[injectSEOMetadata] Injection completed: ${successCount}/4 successful`)

  if (results.errors.length > 0) {
    console.error(`[SEO] Injection errors for ${context.url || 'unknown'}:`, results.errors)
  }

  return results
}

/**
 * Utility function for rapid injection of essential metadata
 */
export const injectEssentialSEO = function (title, description, canonical) {
  return injectSEOMetadata({
    title: title,
    description: description,
    canonical: canonical
  })
}

const JSONLD_ID = 'jsonld-gardener'

export const injectJsonLd = (data) => {
  removeJsonLd()
  if (Array.isArray(data)) {
    data.forEach(function (item, i) {
      var script = document.createElement('script')
      script.type = 'application/ld+json'
      script.className = JSONLD_ID
      script.text = JSON.stringify(item)
      document.head.appendChild(script)
    })
  } else {
    var script = document.createElement('script')
    script.type = 'application/ld+json'
    script.className = JSONLD_ID
    script.text = JSON.stringify(data)
    document.head.appendChild(script)
  }
}

export const removeJsonLd = () => {
  var existing = document.querySelectorAll('.' + JSONLD_ID)
  for (var i = 0; i < existing.length; i++) {
    existing[i].remove()
  }
}

const OG_CLASS = 'seo-og-meta'

function setMetaProperty (property, content) {
  if (!content) return
  var meta = document.querySelector('meta[property="' + property + '"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('property', property)
    meta.className = OG_CLASS
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

function setMetaName (name, content) {
  if (!content) return
  var meta = document.querySelector('meta[name="' + name + '"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', name)
    meta.className = OG_CLASS
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

export const injectOpenGraph = ({ title, description, url, image, type }) => {
  setMetaProperty('og:title', title)
  setMetaProperty('og:description', description)
  setMetaProperty('og:url', url)
  setMetaProperty('og:image', image)
  setMetaProperty('og:type', type || 'website')
  setMetaProperty('og:site_name', 'CMonJardinier')
  setMetaProperty('og:locale', 'fr_FR')
  // Twitter Card
  setMetaName('twitter:card', image ? 'summary_large_image' : 'summary')
  setMetaName('twitter:title', title)
  setMetaName('twitter:description', description)
  if (image) setMetaName('twitter:image', image)
}

export const removeOpenGraph = () => {
  var existing = document.querySelectorAll('.' + OG_CLASS)
  for (var i = 0; i < existing.length; i++) {
    existing[i].remove()
  }
}



// WEBPACK FOOTER //
// ./src/utilities/window.js